<template>
    <div class="temp-content">
        <!--页面说明-->
        <div class="tip-info">
            <p class="tip-text">用于搜索页搜索热词配置</p>
        </div>

        <!-- 新增按钮 -->
        <div class="action-button">
            <t-button theme="primary" @click="addClick">
                <template #icon><add-icon /></template>
                    新增
            </t-button>
        </div>

        <!-- 列表区域 -->
        <t-table
            :data="searchKeywordData" 
            :columns="columns"
            table-layout="fixed"
            :show-header="true"
            cell-empty-content="--"
            row-key="index"
            resizable
            lazy-load
            size="large"
            bordered
            hover
            @drag-sort="onDragSort"
            drag-sort="row-handler"
        >
            <template #drag>
                <span><MoveIcon /></span>
            </template>

            <template #orderNumber="{ rowIndex }">
                {{ rowIndex + 1 }}
            </template>

            <!--启用状态-->
            <template #isActive="{ row }">
                <span :class="{ 'status-inactive': row.isActive === '已停用' }">
                    {{ row.isActive }}
                </span>
            </template>

            <template #action="{ row }">
                <div class="tableActionButton">
                    <t-link
                        class="detailAction"
                        theme="primary"
                        hover="color"
                        @click="editClick(row)"
                    >
                        编辑
                    </t-link>

                    <t-link
                        class="statusAction"
                        theme='primary'
                        hover="color"
                        @click="switchStatus(row)"
                    >
                        {{ row.isActive === '已启用' ? '停用' : '启用' }}
                    </t-link>

                    <t-link
                        class="deleteAction"
                        v-if="row.isActive === '已停用'"
                        theme="danger"
                        hover="color"
                        @click="deleteItem(row)"
                    >
                        删除
                    </t-link>
                </div>
            </template>
        </t-table>

        <!-- 抽屉-新增搜索词 -->
        <t-drawer
            class="add-drawer"
            v-model:visible="addDrawerVisible"
            header="新增搜索词" 
            size="800px"
        >
            <div class="form-wrap">
                <!-- 搜索词名称 -->
                <div class="form-item">
                    <div class="form-item-name">搜索词</div>
                    <div class="form-element">
                        <t-input
                            v-model="addKeywordData.name"
                            placeholder="请输入搜索词"
                            :maxlength="8"
                            show-limit-number
                            clearable
                            size="large"
                        />
                    </div>
                </div>
                            
                <!-- 排序 -->
                <div class="form-item">
                    <div class="form-item-name">排序</div>
                    <div class="number-form-element">
                        <t-input-number
                            v-model="addKeywordData.order"
                            :max="9999"
                            :min="1"
                            size="large" 
                        />             
                    </div>
                </div>
            </div>

            <template #footer>
                <t-button
                    theme="primary"
                    @click="submitAddKeyword"
                    :loading="isSubmitting"
                    :disabled="isSubmitting"
                >
                    {{ isSubmitting ? '提交中' : '确定' }}
                </t-button>
                <t-button theme="default" @click="addDrawerVisible = false">取消</t-button>
            </template>
        </t-drawer>

        <!-- 抽屉-编辑搜索词 -->
        <t-drawer
            class="edit-drawer"
            v-model:visible="editDrawerVisible"
            header="编辑搜索词" 
            size="800px"
        >
            <div class="form-wrap">
                <!-- 搜索词名称 -->
                <div class="form-item">
                    <div class="form-item-name">搜索词</div>
                    <div class="form-element">
                        <t-input
                            v-model="editKeywordData.name"
                            placeholder="请输入搜索词"
                            :maxlength="8"
                            show-limit-number
                            clearable
                            size="large"
                        />
                    </div>
                </div>
                            
                <!-- 排序 -->
                <div class="form-item">
                    <div class="form-item-name">排序</div>
                    <div class="number-form-element">
                        <t-input-number
                            v-model="editKeywordData.order"
                            :max="9999"
                            :min="1"
                            size="large" 
                        />             
                    </div>
                </div>
            </div>

            <template #footer>
                <t-button theme="primary" @click="submitEditKeyword">确定</t-button>
                <t-button theme="default" @click="editDrawerVisible = false">取消</t-button>
            </template>
        </t-drawer>
    </div>
</template>

<script setup lang="jsx">
    import { AddIcon, MoveIcon } from 'tdesign-icons-vue-next';
    import { ref, onMounted } from 'vue';
    import searchKeywordAPI from '../../../api/search-keyword-api';
    import { MessagePlugin, DialogPlugin } from 'tdesign-vue-next';
    import { formatTime } from '../../../utils/datetime';

    // 数据初始化
    const searchKeywordData = ref([]);

    /**
     * 表格列定义
     */
    const columns = ref([
        { colKey: 'drag', title: '', width: '48' },
        { colKey: 'orderNumber', title: '序号', width: '72' },
        { colKey: 'name', title: '搜索词' },
        { colKey: 'clickCount', title: '点击次数', width: '100' },
        { colKey: 'isActive', title: '状态' },
        { colKey: 'updateTime', title: '修改时间' },
        { colKey: 'updateBy', title: '修改人' },
        { colKey: 'action', title: '操作', width: 180 },
    ]);

    /**
     * 获取搜索词列表
     */
    const getSearchKeywordList = async () => {
        try {
            const response = await searchKeywordAPI.getSearchKeywordList();
            console.log('API响应数据:', response); 

            if (response && Array.isArray(response.search_keyword_data)) {
            searchKeywordData.value = response.search_keyword_data.map((item, index) => ({
                id: item.id,
                name: item.name,
                isActive: item.is_active ? '已启用' : '已停用',
                order: item.order,
                updateTime: item.update_time ? formatTime(item.update_time) : '--',
                updateBy: item.update_by || '--',
                orderNumber: index + 1
            }));

                // 计算最大排序值
                const maxOrder = Math.max(...searchKeywordData.value.map(item => item.order), 0);
                addKeywordData.value.order = maxOrder + 1;
            } else {
                MessagePlugin.error('获取搜索词失败：数据格式错误');
            }
        } catch (error) {
            console.error('获取搜索词列表失败', error);
            MessagePlugin.error('获取搜索词列表失败: ' + error.message);
        }
    };

    // 生命周期钩子
    onMounted(() => {
        getSearchKeywordList();
    });

    // 抽屉可见性状态
    const addDrawerVisible = ref(false);
    const editDrawerVisible = ref(false);

    // 搜索词最大数量
    const maxKeywordNumber = 50;

    /**
     * 新增按钮跳转
     */
    const addClick = () => {
        if (searchKeywordData.value.length >= maxKeywordNumber) {
            MessagePlugin.warning(`最多添加${maxKeywordNumber}个搜索词`);
            return;
        }
        addKeywordData.value = {
            name: '',
            order: Math.max(...searchKeywordData.value.map(item => item.order), 0) + 1
        };
        addDrawerVisible.value = true;
    };

    // 新增搜索词初始数据
    const addKeywordData = ref({
        name: '',
        order: 1
    });

    // 验证新增搜索词表单
    const validateAddKeyword = () => {
        if (!addKeywordData.value.name) {
            MessagePlugin.warning('请输入搜索词');
            return false;
        }
        return true;
    };

    // 按钮提交中
    const isSubmitting = ref(false);

    // 提交新增搜索词
    const submitAddKeyword = async () => {
        if (isSubmitting.value) {
            return; // 如果正在提交，直接返回
        }

        if (!validateAddKeyword()) {
            return;
        }

        try {
            isSubmitting.value = true;
            await searchKeywordAPI.createSearchKeyword(addKeywordData.value);

            MessagePlugin.success('新增搜索词成功');
            addDrawerVisible.value = false;
            await getSearchKeywordList(); 
            addKeywordData.value = {
                name: '',
                order: 1
            };
        } catch (error) {
            console.error('新增失败', error);
            MessagePlugin.error('新增失败: ' + (error.response?.data?.message || error.message));
        } finally {
            setTimeout(() => {
                isSubmitting.value = false;
            }, 2000);
        }
    };

    // 编辑搜索词初始数据
    const editKeywordData = ref({
        id: null,
        name: '',
        order: 1
    });

    /**
     * 编辑按钮跳转
     */
    const editClick = (row) => {
        editKeywordData.value = {
            id: row.id,
            name: row.name,
            order: row.order
        };
        editDrawerVisible.value = true;
    };

    // 编辑搜索词验证
    const validateEditKeyword = () => {
        if (!editKeywordData.value.name) {
            MessagePlugin.warning('请输入搜索词');
            return false;
        }
        return true;
    };

    /**
     * 提交编辑搜索词
     */
    const submitEditKeyword = async () => {
        if (!validateEditKeyword()) {
            return;
        }
        try {
            await searchKeywordAPI.updateSearchKeyword(editKeywordData.value.id, editKeywordData.value);
            
            MessagePlugin.success('编辑搜索词成功');
            editDrawerVisible.value = false;
            await getSearchKeywordList();
        } catch (error) {
            console.error('编辑搜索词失败', error);
            MessagePlugin.error('编辑失败: ' + (error.response?.data?.message || error.message));
        }
    };

    /**
     * 修改状态
     */
    const switchStatus = (row) => {
        const newStatus = row.isActive === '已停用';
        const actionText = newStatus ? '启用' : '停用';

        const dialog = DialogPlugin.confirm({
            header: '提示',
            body: `确定${actionText}搜索词 "${row.name}" 吗？`,
            confirmBtn: '确定',
            cancelBtn: '取消',
            onConfirm: async () => {
                try {
                    await searchKeywordAPI.updateKeywordStatus(row.id, newStatus);
                    row.isActive = newStatus ? '已启用' : '已停用';
                    searchKeywordData.value = [...searchKeywordData.value];
                    dialog.destroy();
                    MessagePlugin.success(`搜索词已${actionText}`);
                } catch (error) {
                    console.error('修改状态失败:', error);
                    MessagePlugin.error(`${actionText}失败，请重试`);
                }
            },
        });
    };  

    /**
     * 删除搜索词
     */
    const deleteItem = (row) => {
        const dialog = DialogPlugin.confirm({
            header: '提示',
            body: `确定要删除搜索词 "${row.name}" 吗？`,
            confirmBtn: '确定',
            cancelBtn: '取消',
            onConfirm: async () => {
                try {
                    await searchKeywordAPI.deleteSearchKeyword(row.id);
                    MessagePlugin.success('删除成功');
                    dialog.destroy();
                    await getSearchKeywordList();
                } catch (error) {
                    MessagePlugin.error('删除失败: ' + (error.response?.data?.message || error.message));
                }
            }
        });
    };

    /**
     * 拖动排序
     */
    const onDragSort = async ({ newData }) => {
        try {
            searchKeywordData.value = newData;
            
            const updatedKeywordData = searchKeywordData.value.map((item, index) => ({
                id: item.id,
                order: index + 1
            }));

            await searchKeywordAPI.reorderSearchKeyword(updatedKeywordData);
            MessagePlugin.success('排序更新成功');

            searchKeywordData.value = searchKeywordData.value.map((item, index) => ({
                ...item,
                orderNumber: index + 1
            }));

        } catch (error) {
            console.error('排序更新失败', error);
            MessagePlugin.error(`排序更新失败: ${error.message}`);
            
            try {
                await getSearchKeywordList();
                MessagePlugin.info('已重新加载数据');
            } catch (getListError) {
                MessagePlugin.error('刷新数据失败，请重试');
            }
        }
    };
</script>

<style scoped>
    @import "../../../components/style/style-content.css";
    @import "../../../components/style/style-table.css";
    @import "../../../components/style/style-drawer.css";
</style>